import { useState } from 'react';
import { Edit2, Check, X } from 'lucide-react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

import Docker from '@/assets/ico/vendor/docker.svg?c';
import Podman from '@/assets/ico/vendor/podman.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';
import { notifySuccess } from '@/portainer/services/notifications';

import { Badge } from '@@/Badge';
import { DetailsTable } from '@@/DetailsTable';
import { InlineLoader } from '@@/InlineLoader';
import { Alert } from '@@/Alert';
import { Widget, WidgetBody } from '@@/Widget';
import { Input } from '@@/form-components/Input';
import { Button } from '@@/buttons';

import { usePolicy } from '../queries/usePolicy';
import { usePolicyMetadata } from '../queries/usePolicyMetadata';
import { useUpdatePolicyMutation } from '../queries/useUpdatePolicyMutation';
import {
  getPolicyTypeDisplayName,
  getEnvironmentTypeDisplayName,
  PolicyEnvironmentType,
  getMinimumAgentVersion,
} from '../types';
import { CreatePolicyData } from '../queries/types';

/** Props for the PolicyDetailsTab component */
interface Props {
  /** The ID of the policy to display details for */
  policyId: number;
}

const nameValidationSchema = Yup.object({
  name: Yup.string().required('Name is required').trim(),
});

export function PolicyDetailsTab({ policyId }: Props) {
  const policyQuery = usePolicy(policyId);
  const metadataQuery = usePolicyMetadata();
  const updateMutation = useUpdatePolicyMutation();

  const [isEditingName, setIsEditingName] = useState(false);

  const EnvironmentIcon = getEnvironmentTypeIcon(
    policyQuery.data?.EnvironmentType
  );
  const minimumAgentVersion = policyQuery.data?.Type
    ? getMinimumAgentVersion(
        policyQuery.data.Type,
        metadataQuery.data?.minimumAgentVersions
      )
    : null;

  async function handleSaveName(values: { name: string }) {
    if (!policyQuery.data || values.name === policyQuery.data.Name) {
      setIsEditingName(false);
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: policyQuery.data.Id,
        payload: {
          Name: values.name,
          Type: policyQuery.data.Type,
          EnvironmentType: policyQuery.data.EnvironmentType,
          EnvironmentGroups: policyQuery.data.EnvironmentGroups,
          Data: policyQuery.data.Data as CreatePolicyData,
        },
      });
      notifySuccess('Success', 'Policy name updated');
      setIsEditingName(false);
    } catch {
      // Error is handled by global error handler in mutation
    }
  }

  if (policyQuery.isLoading) {
    return <InlineLoader>Loading...</InlineLoader>;
  }
  if (policyQuery.error) {
    return (
      <Alert color="error" title="Error loading policy">
        {(policyQuery.error as Error)?.message ?? 'Unknown error'}
      </Alert>
    );
  }
  if (!policyQuery.data) {
    return (
      <Alert color="error" title="Policy not found">
        Policy not found
      </Alert>
    );
  }

  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget>
          <WidgetBody>
            <DetailsTable dataCy="policy-details-table">
              {policyQuery.data?.Name && (
                <tr>
                  <td className="min-w-[150px]">Policy name</td>
                  <td data-cy="detailsTable-Policy nameValue">
                    {isEditingName ? (
                      <Formik
                        initialValues={{ name: policyQuery.data.Name }}
                        validationSchema={nameValidationSchema}
                        onSubmit={handleSaveName}
                        validateOnMount
                      >
                        {({ submitForm, isValid }) => (
                          <Form>
                            <div className="flex items-center gap-2">
                              <Field
                                as={Input}
                                name="name"
                                className="!h-8 w-64"
                                autoFocus
                                onKeyDown={(e: React.KeyboardEvent) => {
                                  if (e.key === 'Enter' && isValid) {
                                    submitForm();
                                  }
                                  if (e.key === 'Escape') {
                                    setIsEditingName(false);
                                  }
                                }}
                                data-cy="policy-name-edit-input"
                              />
                              <Button
                                size="small"
                                color="primary"
                                onClick={submitForm}
                                disabled={updateMutation.isLoading || !isValid}
                                title="Save name"
                                icon={Check}
                                data-cy="policy-name-save-button"
                              />
                              <Button
                                size="small"
                                color="default"
                                onClick={() => setIsEditingName(false)}
                                disabled={updateMutation.isLoading}
                                title="Cancel"
                                icon={X}
                                data-cy="policy-name-cancel-button"
                              />
                            </div>
                          </Form>
                        )}
                      </Formik>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{policyQuery.data.Name}</span>
                        <Button
                          color="link"
                          size="xsmall"
                          icon={Edit2}
                          onClick={() => setIsEditingName(true)}
                          title="Edit policy name"
                          data-cy="policy-name-edit-button"
                        />
                      </div>
                    )}
                  </td>
                </tr>
              )}

              {policyQuery.data?.Type && (
                <tr>
                  <td className="min-w-[150px]">Type</td>
                  <td data-cy="detailsTable-TypeValue">
                    <Badge type="info">
                      {getPolicyTypeDisplayName(policyQuery.data.Type)}
                    </Badge>
                  </td>
                </tr>
              )}

              {policyQuery.data?.EnvironmentType && (
                <>
                  <tr>
                    <td
                      colSpan={2}
                      className="min-w-[150px] font-semibold pt-4 pb-2"
                    >
                      Policy Requirements
                    </td>
                  </tr>
                  <tr>
                    <td className="min-w-[150px]">
                      <span className="ml-4">Environment Type</span>
                    </td>
                    <td data-cy="detailsTable-EnvironmentTypeValue">
                      <Badge type="muted" className="min-w-max">
                        <EnvironmentIcon className="h-4 w-4" />
                        {getEnvironmentTypeDisplayName(
                          policyQuery.data.EnvironmentType
                        )}
                      </Badge>
                    </td>
                  </tr>
                  {minimumAgentVersion && (
                    <tr>
                      <td className="min-w-[150px]">
                        <span className="ml-4">Agent</span>
                      </td>
                      <td data-cy="detailsTable-AgentValue">
                        &gt;= {minimumAgentVersion}
                      </td>
                    </tr>
                  )}
                </>
              )}

              {policyQuery.data?.UpdatedAt && (
                <tr>
                  <td className="min-w-[150px]">Last updated</td>
                  <td data-cy="detailsTable-Last updatedValue">
                    <span className="text-muted">
                      {policyQuery.data.UpdatedAt
                        ? new Date(policyQuery.data.UpdatedAt).toLocaleString()
                        : 'Unknown'}
                    </span>
                  </td>
                </tr>
              )}
            </DetailsTable>
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );
}

/**
 * Get the appropriate icon component based on environment type.
 * @param environmentType - The policy environment type
 * @returns SVG icon component for the environment type
 */
export function getEnvironmentTypeIcon(
  environmentType?: PolicyEnvironmentType
) {
  switch (environmentType) {
    case 'kubernetes':
      return Kubernetes;
    case 'podman':
      return Podman;
    case 'docker':
    case 'swarm':
    default:
      return Docker;
  }
}
